import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { BookSchema } from "../../Utils/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import { GenericFormProps } from "../../Utils/Types";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { onFormError } from "../../Utils/Utils";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { AxiosResponse } from "axios";
import { updateData } from "../../Services/updateData";
import { FormsIds } from "../../Utils/Constants";
import { useEffect } from "react";

/**
 * Form to create or update a book.
 *
 * Props:
 * - {GenericFormProps} props
 *
 * Renders a form with the following fields:
 * - name: {string}
 * - workPointId: {string}
 * - maxPerStudent: {number}
 * - alertThreshold: {number}
 * - price: {number}
 *
 * When the form is submitted, it calls either createElement or updateElement
 * depending on whether the model has an _id property.
 *
 * @param {GenericFormProps} props
 * @returns {JSX.Element}
 */
export default function BookForm(props: GenericFormProps) {
  const { selectedRow, onClose } = props;
  const { register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(BookSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const memo = useCustomMemo();
  const workPoints = memo.storage.workPoints;

  /**
   * Resets the form when the selectedRow changes.
   */
  useEffect(() => {
    if (selectedRow) {
      const cleanData = {
        ...selectedRow,
        workPointId: selectedRow?.workPointId?._id,
      };
      reset(cleanData);
    }
  }, [selectedRow]);

  /**
   * Sends a POST request to create a new book element.
   * If the request is successful and there are no errors, it closes the modal.
   * @param {Object} model - The model data for the new book.
   */

  const createElement = (model: any) => {
    const url = `${endpoints.books}`;
    postData(url, model).then((res: AxiosResponse) => {
      if (!res || res?.data.errors) {
        return;
      }
      onClose();
    });
  };

  /**
   * Calls the updateData function with the given model and the books endpoint, if
   * the request is successful it will close the modal.
   * @param {Object} model - The model to be updated.
   */
  const updateElement = (model: any) => {
    const url = `${endpoints.books}/${model._id}`;
    updateData(url, model).then((res: AxiosResponse) => {
      if (!res || res?.data.errors) {
        return;
      }
      props.onClose();
    });
  };

  /**
   * Submits the form with the given model. If the model has an _id
   * property, it calls updateElement, otherwise it calls createElement.
   * @param {Object} model - The model to be submitted.
   */
  const onSubmit = (model: any) => {
    model._id ? updateElement(model) : createElement(model);
  };

  /***
   * Render
   */
  return (
    <form onSubmit={handleSubmit(onSubmit, onFormError)} id={FormsIds.bookForm}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            {...register("name")}
            id="name"
            name="name"
            label={Vocabulary.name}
            fullWidth
            error={typeof formState.errors.name === "object"}
            helperText={formState.errors.name?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <FormControl
            fullWidth
            variant="outlined"
            error={!!formState.errors.workPointId}
          >
            <InputLabel>{Vocabulary.workPoint}</InputLabel>
            <Select
              id="workPointId"
              label={Vocabulary.workPoint}
              {...register("workPointId")}
              defaultValue={selectedRow?.workPointId?._id ?? ""}
            >
              {workPoints.map((workPoint: any) => (
                <MenuItem key={workPoint?._id} value={workPoint?._id}>
                  {workPoint.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {formState.errors.workPointId?.message?.toString()}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            {...register("maxPerStudent")}
            id="maxPerStudent"
            name="maxPerStudent"
            label={Vocabulary.maxPerStudent}
            fullWidth
            error={typeof formState.errors.maxPerStudent === "object"}
            helperText={formState.errors.maxPerStudent?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{ endAdornment: Vocabulary.pieces }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            {...register("alertThreshold")}
            id="alertThreshold"
            name="alertThreshold"
            label={Vocabulary.alertThreshold}
            fullWidth
            error={typeof formState.errors.alertThreshold === "object"}
            helperText={formState.errors.alertThreshold?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{ endAdornment: Vocabulary.pieces }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            {...register("price")}
            id="price"
            name="price"
            label={Vocabulary.price}
            fullWidth
            error={typeof formState.errors.price === "object"}
            helperText={formState.errors.price?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{ endAdornment: Vocabulary.lei }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            {...register("quantity")}
            id="quantity"
            name="quantity"
            label={Vocabulary.quantity}
            fullWidth
            error={typeof formState.errors.quantity === "object"}
            helperText={formState.errors.quantity?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
}
